/**
 * Authentication context for managing user auth state.
 *
 * Handles login/logout, token refresh, and persisting auth state.
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import * as authApi from '../api/auth'
import { apiClient } from '../api/client'

interface AuthContextType {
  user: authApi.User | null
  isLoading: boolean
  isAuthenticated: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string) => Promise<string>
  logout: () => Promise<void>
  verifyEmail: (code: string) => Promise<void>
  refreshUser: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | null>(null)

const REFRESH_TOKEN_KEY = 'polititrack_refresh_token'
const ACCESS_TOKEN_KEY = 'polititrack_access_token'

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<authApi.User | null>(null)
  const [accessToken, setAccessToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  // Set up axios interceptor for auth header
  useEffect(() => {
    const requestInterceptor = apiClient.interceptors.request.use(
      (config) => {
        if (accessToken) {
          config.headers.Authorization = `Bearer ${accessToken}`
        }
        return config
      },
      (error) => Promise.reject(error)
    )

    return () => {
      apiClient.interceptors.request.eject(requestInterceptor)
    }
  }, [accessToken])

  // Set up response interceptor for token refresh
  useEffect(() => {
    const responseInterceptor = apiClient.interceptors.response.use(
      (response) => response,
      async (error) => {
        const originalRequest = error.config

        // If 401 and we haven't retried yet, try to refresh
        if (error.response?.status === 401 && !originalRequest._retry) {
          originalRequest._retry = true

          const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
          if (refreshToken) {
            try {
              const tokens = await authApi.refreshTokens(refreshToken)
              setAccessToken(tokens.access_token)
              localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)

              // Retry original request with new token
              originalRequest.headers.Authorization = `Bearer ${tokens.access_token}`
              return apiClient(originalRequest)
            } catch {
              // Refresh failed, clear auth state
              setUser(null)
              setAccessToken(null)
              localStorage.removeItem(REFRESH_TOKEN_KEY)
              localStorage.removeItem(ACCESS_TOKEN_KEY)
            }
          }
        }

        return Promise.reject(error)
      }
    )

    return () => {
      apiClient.interceptors.response.eject(responseInterceptor)
    }
  }, [])

  // Initialize auth state from stored tokens
  useEffect(() => {
    const initAuth = async () => {
      const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
      const storedAccessToken = localStorage.getItem(ACCESS_TOKEN_KEY)

      if (refreshToken) {
        try {
          // Try to use stored access token first
          if (storedAccessToken) {
            setAccessToken(storedAccessToken)
            const userData = await authApi.getCurrentUser()
            setUser(userData)
          } else {
            // Otherwise refresh
            const tokens = await authApi.refreshTokens(refreshToken)
            setAccessToken(tokens.access_token)
            localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
            localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token)

            const userData = await authApi.getCurrentUser()
            setUser(userData)
          }
        } catch {
          // Tokens invalid, clear storage
          localStorage.removeItem(REFRESH_TOKEN_KEY)
          localStorage.removeItem(ACCESS_TOKEN_KEY)
        }
      }

      setIsLoading(false)
    }

    initAuth()
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const tokens = await authApi.login({ email, password })
    setAccessToken(tokens.access_token)
    localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refresh_token)
    localStorage.setItem(ACCESS_TOKEN_KEY, tokens.access_token)

    const userData = await authApi.getCurrentUser()
    setUser(userData)
  }, [])

  const register = useCallback(async (email: string, password: string): Promise<string> => {
    const response = await authApi.register({ email, password })
    return response.message
  }, [])

  const logout = useCallback(async () => {
    const refreshToken = localStorage.getItem(REFRESH_TOKEN_KEY)
    if (refreshToken && accessToken) {
      try {
        await authApi.logout(refreshToken)
      } catch {
        // Ignore errors on logout
      }
    }

    setUser(null)
    setAccessToken(null)
    localStorage.removeItem(REFRESH_TOKEN_KEY)
    localStorage.removeItem(ACCESS_TOKEN_KEY)
  }, [accessToken])

  const verifyEmail = useCallback(async (code: string) => {
    await authApi.verifyEmail(code)
    // Refresh user to get updated email_verified status
    if (accessToken) {
      const userData = await authApi.getCurrentUser()
      setUser(userData)
    }
  }, [accessToken])

  const refreshUser = useCallback(async () => {
    if (accessToken) {
      const userData = await authApi.getCurrentUser()
      setUser(userData)
    }
  }, [accessToken])

  const value: AuthContextType = {
    user,
    isLoading,
    isAuthenticated: !!user,
    login,
    register,
    logout,
    verifyEmail,
    refreshUser,
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}
